/** One in-memory viewer attempt. A failed request never creates another attempt. */
export class ViewOnceSession {
  constructor(id, request, token) {
    this.id = id;
    this.request = request;
    this.token = token;
    this.closed = false;
    this.controller = new AbortController();
    this.claiming = null;
    this.claimOutcome = Promise.resolve(null);
  }

  async load() {
    if (this.closed) return null;
    const path = '/view-once/' + encodeURIComponent(this.id);
    // Not aborted on close: the DELETE must reach the server after this claim, or the claim stays open.
    const claim = this.request(path + '/session', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: this.token }), cache: 'no-store',
    });
    this.claiming = claim;
    const outcome = claim.then(response => (response.ok ? 'claimed' : String(response.status)), () => 'failed');
    // One accepted claim spends the view, whatever a later retry gets back.
    this.claimOutcome = Promise.all([this.claimOutcome, outcome]).then(([before, now]) => (before === 'claimed' ? before : now));
    let session;
    try {
      session = await claim;
    } finally {
      if (this.claiming === claim) this.claiming = null;
    }
    if (!session.ok) throw new Error(String(session.status));
    const metadata = await session.json();
    if (this.closed) return null;
    const content = await this.request(path + '/content', {
      headers: { 'X-View-Once-Token': this.token }, signal: this.controller.signal, cache: 'no-store',
    });
    if (!content.ok) throw new Error(String(content.status));
    const link = await content.json();
    if (typeof link?.url !== 'string' || link.url === '') throw new Error('invalid_link');
    return this.closed ? null : { metadata, url: link.url, expiresAt: Number(link.expires_at) };
  }

  /**
   * Resolves true when no claim was accepted and the latest one got no answer: none sent, a network failure or a
   * 5xx. The view is then not spent. A refusal such as 409 means the view is gone.
   */
  async unclaimed() {
    const outcome = await this.claimOutcome;
    return outcome === null || outcome === 'failed' || outcome.startsWith('5');
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.controller.abort();
    const token = this.token;
    this.token = null;
    const release = () => this.request('/view-once/' + encodeURIComponent(this.id) + '/session', {
      method: 'DELETE', headers: { 'X-View-Once-Token': token }, keepalive: true,
    }).catch(() => {});
    if (this.claiming) void this.claiming.then(release, release);
    else void release();
  }
}
